// src/components/CBCTReport.js
import React from 'react';
import {
    AppBar,
    Box,
    CssBaseline,
    Drawer,
    Toolbar,
    Typography,
    CircularProgress,
    Alert,
    Tabs,
    Tab,
    IconButton,
    Button
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { useFormContext } from '../context/FormContext';
import Sidebar from './Sidebar';
import CanalsTracedTab from './CanalsTracedTab';
import PanoramicViewTab from './PanoramicViewTab';
import DimensionsTab from './DimensionsTab';
import RegionDetailsTab from './RegionDetailsTab';
import GenerateReportTab from './GenerateReportTab';
import VirtualImplantTab from './VirtualImplantTab';
import SaveButton from './SaveButton';
import ScrollToTopButton from './ScrollToTopButton';
import { useAuth } from "react-oidc-context";
import { performSignOut } from "../signOut";

const drawerWidth = 340;

function TabPanel({ children, value, index }) {
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`tabpanel-${index}`}
            aria-labelledby={`tab-${index}`}
        >
            {value === index && (
                <Box sx={{ p: 0 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

function CBCTReport() {
    const { state, actions } = useFormContext();
    const auth = useAuth();

    const handleSidebarToggle = () => {
        actions.toggleSidebar();
    };

    const handleTabChange = (event, newValue) => {
        actions.setCurrentTab(newValue);
    };

    return (
        <Box sx={{ display: 'flex' }}>
            <CssBaseline />

            {/* App Bar */}
            <AppBar
                position="fixed"
                sx={{
                    width: `calc(100% - ${state.isSidebarOpen ? drawerWidth : 0}px)`,
                    ml: state.isSidebarOpen ? `${drawerWidth}px` : 0,
                    transition: (theme) => theme.transitions.create(['margin', 'width'], {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.leavingScreen,
                    }),
                }}
            >
                <Toolbar>
                    <IconButton
                        color="inherit"
                        aria-label="open drawer"
                        onClick={handleSidebarToggle}
                        edge="start"
                        sx={{ mr: 2 }}
                    >
                        <MenuIcon />
                    </IconButton>
                    <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
                        CBCT REPORT GENERATOR
                    </Typography>

                    {/* Home Button added for navigation */}
                    <Button color="inherit" onClick={() => performSignOut()}>Sign out</Button>
                    <SaveButton />
                </Toolbar>
            </AppBar>

            {/* Sidebar Drawer */}
            <Drawer
                sx={{
                    width: drawerWidth,
                    flexShrink: 0,
                    '& .MuiDrawer-paper': {
                        width: drawerWidth,
                        boxSizing: 'border-box',
                    },
                }}
                variant="persistent"
                anchor="left"
                open={state.isSidebarOpen}
            >
                <Sidebar />
            </Drawer>

            {/* Main Content */}
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: 3,
                    transition: (theme) => theme.transitions.create('margin', {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.leavingScreen,
                    }),
                    marginLeft: `-${drawerWidth}px`,
                    ...(state.isSidebarOpen && {
                        transition: (theme) => theme.transitions.create('margin', {
                            easing: theme.transitions.easing.easeOut,
                            duration: theme.transitions.duration.enteringScreen,
                        }),
                        marginLeft: 0,
                    }),
                }}
            >
                <Toolbar />

                {/* Loading Indicator */}
                {state.loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                        <CircularProgress />
                    </Box>
                )}

                {/* Error Alert */}
                {state.error && (
                    <Alert
                        severity="error"
                        sx={{ mt: 4 }}
                        onClose={() => actions.clearError()}
                    >
                        {state.error}
                    </Alert>
                )}

                {/* Show Virtual Implant Tab for Virtual Implant report type */}
                {state.reportType === 'Virtual Implant' && (
                    <Box sx={{ mt: 4 }}>
                        <VirtualImplantTab />
                    </Box>
                )}

                {/* Show normal tabs for basic reports and when patient data is loaded */}
                {state.patientData && (state.reportType === 'Mandible - Basic Report' || state.reportType === 'Maxilla - Basic Report') && (
                    <>
                        <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 4 }}>
                            <Tabs
                                value={state.currentTab}
                                onChange={handleTabChange}
                                aria-label="report tabs"
                                variant="scrollable"
                                scrollButtons="auto"
                            >
                                <Tab label="Canals Traced" id="tab-0" aria-controls="tabpanel-0" />
                                <Tab label="Panoramic View" id="tab-1" aria-controls="tabpanel-1" />
                                <Tab label="Dimensions" id="tab-2" aria-controls="tabpanel-2" />
                                <Tab label="Region Details" id="tab-3" aria-controls="tabpanel-3" />
                                <Tab label="Generate Report" id="tab-4" aria-controls="tabpanel-4" />
                            </Tabs>
                        </Box>

                        {/* Tab Panels */}
                        <TabPanel value={state.currentTab} index={0}>
                            <CanalsTracedTab />
                        </TabPanel>

                        <TabPanel value={state.currentTab} index={1}>
                            <PanoramicViewTab />
                        </TabPanel>

                        <TabPanel value={state.currentTab} index={2}>
                            <DimensionsTab />
                        </TabPanel>

                        <TabPanel value={state.currentTab} index={3}>
                            <RegionDetailsTab />
                        </TabPanel>

                        <TabPanel value={state.currentTab} index={4}>
                            <GenerateReportTab />
                        </TabPanel>
                    </>
                )}

                {/* Welcome message when no patient data and not Virtual Implant */}
                {!state.patientData && !state.loading && state.reportType !== 'Virtual Implant' && (
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: '60vh',
                        textAlign: 'center'
                    }}>
                        <Typography variant="h4" gutterBottom color="textSecondary">
                            Configure Report Settings
                        </Typography>
                        <Typography variant="body1" color="textSecondary">
                            Use the sidebar to select the unit, doctor, and patient inputs.
                        </Typography>
                    </Box>
                )}

            </Box>
            <ScrollToTopButton />
        </Box>
    );
}

export default CBCTReport;
